import { useLayoutEffect, useRef } from 'react';
import { Menu } from 'lucide-react';
import { useLocation } from 'react-router';
import type { AuthUser } from '@pointage/shared';
import { useMe } from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { EntitySelect } from './EntitySelect.js';
import { NotificationBell } from './NotificationBell.js';
import { RefreshButton } from './RefreshButton.js';
import { ThemeToggle } from './ThemeToggle.js';
import { UserMenu } from './UserMenu.js';
import { SETTINGS_TOPIC_TITLES } from '../pages/Settings.js';

interface Props {
  user: AuthUser;
  /** Manager recruteur : pas de navigation ni de sélecteur de société. */
  minimal?: boolean;
}

interface Crumb {
  eyebrow: string;
  title: string;
}

// Route → topbar breadcrumb. Mirrors the sidebar grouping.
const CRUMBS: Record<string, Crumb> = {
  '/dashboard': { eyebrow: 'Quotidien', title: 'Tableau de bord' },
  '/bureau': { eyebrow: 'Quotidien', title: 'Bureau' },
  '/entry': { eyebrow: 'Quotidien', title: 'Saisie quotidienne' },
  '/monthly': { eyebrow: 'Quotidien', title: 'Grille mensuelle' },
  '/employees': { eyebrow: 'Données', title: 'Employés' },
  '/inspect': { eyebrow: 'Données', title: 'Inspection employé' },
  '/balances': { eyebrow: 'Données', title: 'Congés' },
  '/recup': { eyebrow: 'Données', title: 'Récupération' },
  '/contrats': { eyebrow: 'Données', title: "Période d'essai & CDD" },
  '/documents': { eyebrow: 'Données', title: 'Demandes de documents' },
  '/reports': { eyebrow: 'Données', title: 'Rapports & fiches' },
  '/activity': { eyebrow: 'Données', title: 'Activité' },
  '/salaires': { eyebrow: 'Paie', title: 'Salaires & bonus' },
  '/settings': { eyebrow: 'Système', title: 'Paramètres' },
};

// Routes sans fil d'Ariane dédié : nommées quand même dans la popup de
// sortie (« Vous passez à Recrutement »).
const EXTRA_TITLES: Record<string, string> = {
  '/organigramme': 'Organigramme',
  '/evaluations': 'Évaluations',
  '/recrutement': 'Recrutement',
};

/** Titre d'une route, tel que la topbar l'affiche (popup de sortie). */
export function routeTitle(pathname: string): string {
  const settingsTopic = pathname.startsWith('/settings/') ? pathname.slice('/settings/'.length) : null;
  if (settingsTopic && SETTINGS_TOPIC_TITLES[settingsTopic]) return SETTINGS_TOPIC_TITLES[settingsTopic];
  return CRUMBS[pathname]?.title ?? EXTRA_TITLES[pathname] ?? 'Pointage';
}

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export function Header({ user, minimal }: Props) {
  const me = useMe();
  const setDrawerOpen = useUiStore((s) => s.setDrawerOpen);
  const barRef = useRef<HTMLElement>(null);

  // Keep --topbar-h equal to the real rendered height. On mobile the
  // bar can wrap, and several islands size themselves with
  // calc(100dvh - var(--topbar-h)), so a stale token leaves them
  // overflowing behind the bottom nav.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const sync = () =>
      document.documentElement.style.setProperty('--topbar-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const entityBarExtras = useUiStore((s) => s.entityBarExtras);
  const { pathname } = useLocation();
  // A settings detail page (/settings/:topic) reflects the topic title
  // in the breadcrumb, mirroring the proto's hashchange behaviour.
  const settingsTopic = pathname.startsWith('/settings/') ? pathname.slice('/settings/'.length) : null;
  const crumb = settingsTopic && SETTINGS_TOPIC_TITLES[settingsTopic]
    ? { eyebrow: 'Système', title: SETTINGS_TOPIC_TITLES[settingsTopic] }
    : minimal ? { eyebrow: 'Recrutement', title: 'Mes postes' }
    : CRUMBS[pathname] ?? { eyebrow: 'SHIFTIN', title: 'Pointage' };

  return (
    <header className="topbar" ref={barRef}>
      {!minimal && <button
        type="button"
        className="icon-button hamburger-button"
        onClick={() => setDrawerOpen(true)}
        aria-label="Ouvrir la navigation"
        data-testid="hamburger-button"
      >
        <Menu aria-hidden="true" />
      </button>}
      <div className="breadcrumb">
        <span className="breadcrumb-eyebrow">{crumb.eyebrow}</span>
        <span className="breadcrumb-title">{crumb.title}</span>
      </div>
      <div className="topbar-spacer" />
      {/* Saisie feeds per-entity counts + caption into the shared selector
          via the store; other routes leave it null → headcounts. */}
      {!minimal && <EntitySelect counts={entityBarExtras?.counts} caption={entityBarExtras?.caption} />}
      <div className="topbar-meta">
        <span className="topbar-date">{DATE_FMT.format(new Date())}</span>
      </div>
      {/* Hors de .topbar-meta à dessein : ce bloc disparaît sous 768px,
          Actualiser et la cloche doivent rester sur la première rangée mobile. */}
      <RefreshButton />
      {me.data && <NotificationBell me={me.data} />}
      <UserMenu user={user} />
      <ThemeToggle variant="inline" />
    </header>
  );
}
